import type { Building } from "building-types/building/buildingTypes";
import { makeWallPlaneTrims } from "../elements/walls2/wallPlaneTrimTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  doc,
  wallPlanes,
  buildingGraph,
  settings,
  roomFloorAssemblies,
  enabledFeatures,
  wallPlaneOpeningDomainOperations,
  resolvedDatums,
  datumAbstractions,
  rooms,
  levels,
}: Pick<
  Building,
  | "doc"
  | "wallPlanes"
  | "buildingGraph"
  | "settings"
  | "roomFloorAssemblies"
  | "enabledFeatures"
  | "wallPlaneOpeningDomainOperations"
  | "resolvedDatums"
  | "datumAbstractions"
  | "rooms"
  | "levels"
>) {
  return {
    wallPlaneTrims: doc.wallPlaneTrims,
    wallPlaneRules: doc.wallPlaneRules,
    wallPlanes,
    buildingPlanes: buildingGraph.buildingPlanes,
    buildingGraph,
    rooms,
    levels,
    roomFloorAssemblies,
    settings,
    profileSource: {
      settingsToCatalogs: enabledFeatures.settingsToCatalogs,
      profiles: doc.profiles,
      profileSettings: settings.profile,
    },
    enabledFeatures,
    wallPlaneOpeningDomainOperations,
    resolvedDatums,
    datumAbstractions,
    wallPlaneAssemblies: doc.wallPlaneAssemblies,
    framingComponents: doc.framingComponents,
    masonryComponents: doc.masonryComponents,
    cipComponents: doc.cipComponents,
    sheathingComponents: doc.sheathingComponents,
  };
}

const compare = makeGenerationStageComparator<typeof select>({ profileSource: "shallow" });

function generate({
  wallPlaneTrims,
  wallPlaneRules,
  wallPlanes,
  buildingPlanes,
  buildingGraph,
  rooms,
  levels,
  profileSource,
  enabledFeatures,
  wallPlaneOpeningDomainOperations,
  resolvedDatums,
  datumAbstractions,
  wallPlaneAssemblies,
  framingComponents,
  masonryComponents,
  cipComponents,
  sheathingComponents,
}: ReturnType<typeof select>): Pick<Building, "wallPlaneTrims" | "interiorWallPlaneTrimMoldings" | "issues"> {
  if (!enabledFeatures.wallPlanes) {
    return { wallPlaneTrims: {}, interiorWallPlaneTrimMoldings: {}, issues: [] };
  }

  return makeWallPlaneTrims({
    wallPlaneTrims,
    wallPlaneRules,
    wallPlanes,
    buildingPlanes,
    buildingGraph,
    rooms,
    levels,
    profileSource,
    wallPlaneOpeningDomainOperations,
    resolvedDatums,
    datumAbstractions,
    wallPlaneAssemblies,
    framingComponents,
    masonryComponents,
    cipComponents,
    sheathingComponents,
  });
}

export default memo(select, generate, compare);
